import { applyLegalMove, applySanMove, piecesInPosition, type AppliedMove } from "./chess/rules";
import { Chess, SQUARES, type Color, type Move, type PieceSymbol, type Square } from "chess.js";
import { canonicalJson } from "./utils/canonical-json";
import type { TimeControl } from "./types/player";
import type { BoardStudyPosition } from "./coach/study";

export const BROWSER_ENGINE_DEPTHS = [2, 4, 8, 16, 24] as const;
export type BrowserEngineDepth = (typeof BROWSER_ENGINE_DEPTHS)[number];
export const BROWSER_ENGINE_MODES = [
  "standard",
  "sacrifice",
  "only_move",
  "defense",
  "mate",
  "refutation",
  "robustness",
  "factors",
  "plans",
  "practical",
] as const;
export type BrowserEngineMode = (typeof BROWSER_ENGINE_MODES)[number];
export type BrowserEngineKind = "position" | "branch" | "move_comparison" | "tablebase";
export type BrowserEnginePlayerColor = "white" | "black";

export interface BrowserEngineSource {
  fen: string;
  playerColor: BrowserEnginePlayerColor;
  gameId?: string;
  ply?: number;
  timeControl?: TimeControl;
  playerRating?: number;
  opponentRating?: number;
  study?: BoardStudyPosition & { sessionId: string; runId: string };
}

export interface BrowserPracticalRequest {
  format: "rapid";
  activeRating: number;
  opponentRating: number;
}

export interface BrowserPracticalEvidence extends BrowserPracticalRequest {
  candidates: Array<{ move: string; probability: number }>;
  source: { name: "Maia 2"; version: "rapid-fp16-v1" };
}

export interface BrowserEngineLimits {
  depth: BrowserEngineDepth;
  maxPlies: number;
  multiPv: number;
  maxTimeMs: number;
  includeWdl: boolean;
}

export interface BrowserEngineRequest {
  schemaVersion: 1;
  requestId: string;
  kind: BrowserEngineKind;
  source: BrowserEngineSource;
  limits: BrowserEngineLimits;
  mode?: BrowserEngineMode;
  candidateMoves?: string[];
  branch?: string[];
  practicalRequest?: BrowserPracticalRequest;
}

export interface BrowserEngineLine {
  rank: number;
  moves: string[];
  evaluation: { cp: number | null; mate: number | null };
  wdl?: { win: number; draw: number; loss: number };
}

export type BrowserAtomicPieceCounts = Record<PieceSymbol, number>;

export interface BrowserAtomicPositionProof {
  version: 1;
  fen: string;
  perspective: BrowserEnginePlayerColor;
  legal: {
    evidenceId: "atomic:legal";
    sideToMove: BrowserEnginePlayerColor;
    inCheck: boolean;
    checkmate: boolean;
    stalemate: boolean;
    legalMoveCount: number;
    moves: Array<{
      san: string;
      from: Square;
      to: Square;
      promotion?: PieceSymbol;
      capture: boolean;
      enPassant: boolean;
      castle: boolean;
    }>;
  };
  material: {
    evidenceId: "atomic:material";
    white: BrowserAtomicPieceCounts;
    black: BrowserAtomicPieceCounts;
    balanceCp: number;
  };
  squareControl: Array<{
    evidenceId: string;
    square: Square;
    whiteAttackers: Square[];
    blackAttackers: Square[];
  }>;
  pins: Array<{
    evidenceId: string;
    color: BrowserEnginePlayerColor;
    king: Square;
    pinned: Square;
    attacker: Square;
    ray: Square[];
  }>;
  rays: Array<{ evidenceId: string; from: Square; to: Square; squares: Square[] }>;
  openFiles: Array<{ evidenceId: string; file: "a" | "b" | "c" | "d" | "e" | "f" | "g" | "h" }>;
  forcingCandidates: Array<{
    evidenceId: string;
    san: string;
    from: Square;
    to: Square;
    kind: "capture" | "check" | "mate";
    target?: PieceSymbol;
  }>;
  forcedness?: {
    evidenceId: "atomic:forcedness";
    basis: "only_legal_move" | "evaluated_candidates";
    forced: boolean;
    evaluatedAlternatives: number;
    meaningfulAlternatives: number;
    thresholdCp: 50 | null;
    lineRanks: number[];
  };
}

export interface BrowserAtomicPositionProofInput {
  fen: string;
  perspective: BrowserEnginePlayerColor;
  lines?: readonly BrowserEngineLine[];
}

export const BROWSER_COACHING_CONCEPTS = [
  "king_safety",
  "activity",
  "space",
  "pawn_structure",
  "material",
  "initiative",
  "passed_pawns",
  "tactical_motifs",
] as const;
export type BrowserCoachingConcept = (typeof BROWSER_COACHING_CONCEPTS)[number];
export interface BrowserCoachingConceptDelta {
  evidenceId: string;
  rank: 1 | 2 | 3;
  concept: BrowserCoachingConcept;
  before: number;
  playedAfter: number;
  bestAfter: number;
  difference: number;
}
export interface BrowserCoachingConceptDeltas {
  version: 1;
  playedMove: { uci: string; san: string };
  bestMove: { uci: string; san: string };
  items: BrowserCoachingConceptDelta[];
}

const ATOMIC_PIECE_VALUES: Record<PieceSymbol, number> = { p: 100, n: 320, b: 330, r: 500, q: 900, k: 0 };
const ATOMIC_FILES = ["a", "b", "c", "d", "e", "f", "g", "h"] as const;
const ATOMIC_DIRECTIONS = [
  [-1, -1],
  [-1, 0],
  [-1, 1],
  [0, -1],
  [0, 1],
  [1, -1],
  [1, 0],
  [1, 1],
] as const;

const atomicColorName = (color: Color): BrowserEnginePlayerColor => (color === "w" ? "white" : "black");
const emptyAtomicCounts = (): BrowserAtomicPieceCounts => ({ p: 0, n: 0, b: 0, r: 0, q: 0, k: 0 });
const atomicCoordinates = (square: Square): readonly [number, number] => [
  square.charCodeAt(0) - 97,
  Number(square[1]) - 1,
];
const atomicSquareAt = (file: number, rank: number): Square | null =>
  file >= 0 && file < 8 && rank >= 0 && rank < 8 ? (`${String.fromCharCode(97 + file)}${rank + 1}` as Square) : null;

const atomicMaterial = (
  board: Chess,
  perspective: BrowserEnginePlayerColor,
): BrowserAtomicPositionProof["material"] => {
  const white = emptyAtomicCounts();
  const black = emptyAtomicCounts();
  for (const square of SQUARES) {
    const piece = board.get(square);
    if (piece) (piece.color === "w" ? white : black)[piece.type] += 1;
  }
  const total = (counts: BrowserAtomicPieceCounts) =>
    (Object.entries(counts) as Array<[PieceSymbol, number]>).reduce(
      (sum, [piece, count]) => sum + ATOMIC_PIECE_VALUES[piece] * count,
      0,
    );
  const whiteBalance = total(white) - total(black);
  return {
    evidenceId: "atomic:material",
    white,
    black,
    balanceCp: perspective === "white" ? whiteBalance : -whiteBalance,
  };
};

const atomicPinFacts = (board: Chess): Pick<BrowserAtomicPositionProof, "pins" | "rays"> => {
  const pins: BrowserAtomicPositionProof["pins"] = [];
  const rays: BrowserAtomicPositionProof["rays"] = [];
  for (const color of ["w", "b"] as const) {
    const king = SQUARES.find((square) => board.get(square)?.color === color && board.get(square)?.type === "k");
    if (!king) continue;
    const [kingFile, kingRank] = atomicCoordinates(king);
    for (const [fileDelta, rankDelta] of ATOMIC_DIRECTIONS) {
      const ray: Square[] = [king];
      let candidate: Square | null = null;
      for (let step = 1; step < 8; step += 1) {
        const square = atomicSquareAt(kingFile + fileDelta * step, kingRank + rankDelta * step);
        if (!square) break;
        ray.push(square);
        const piece = board.get(square);
        if (!piece) continue;
        if (!candidate && piece.color === color) {
          candidate = square;
          continue;
        }
        const diagonal = fileDelta !== 0 && rankDelta !== 0;
        const pinsAlongRay = piece.color !== color && (piece.type === "q" || piece.type === (diagonal ? "b" : "r"));
        if (candidate && pinsAlongRay) {
          const squares = [...ray].reverse();
          pins.push({
            evidenceId: `atomic:pin:${candidate}`,
            color: atomicColorName(color),
            king,
            pinned: candidate,
            attacker: square,
            ray: squares,
          });
          rays.push({ evidenceId: `atomic:ray:${square}:${king}`, from: square, to: king, squares });
        }
        break;
      }
    }
  }
  return { pins, rays };
};

const atomicForcingCandidates = (
  fen: string,
  moves: readonly Move[],
): BrowserAtomicPositionProof["forcingCandidates"] =>
  moves.flatMap((move) => {
    const after = new Chess(fen);
    after.move(move.san);
    const kind = after.isCheckmate() ? "mate" : after.inCheck() ? "check" : move.isCapture() ? "capture" : null;
    if (!kind) return [];
    return [
      {
        evidenceId: `atomic:forcing:${move.from}${move.to}${move.promotion ?? ""}`,
        san: move.san,
        from: move.from,
        to: move.to,
        kind,
        ...(move.captured ? { target: move.captured } : {}),
      },
    ];
  });

const atomicForcedness = (
  legalMoveCount: number,
  lines: readonly BrowserEngineLine[] | undefined,
): BrowserAtomicPositionProof["forcedness"] => {
  if (legalMoveCount === 1)
    return {
      evidenceId: "atomic:forcedness",
      basis: "only_legal_move",
      forced: true,
      evaluatedAlternatives: 0,
      meaningfulAlternatives: 0,
      thresholdCp: null,
      lineRanks: [],
    };
  const ranked = [...(lines ?? [])].sort((left, right) => left.rank - right.rank);
  const best = ranked[0]?.evaluation.cp;
  if (ranked.length < 2 || best === null || best === undefined || ranked.some((line) => line.evaluation.cp === null))
    return undefined;
  const meaningfulAlternatives = ranked.slice(1).filter((line) => Math.abs(line.evaluation.cp! - best) <= 50).length;
  return {
    evidenceId: "atomic:forcedness",
    basis: "evaluated_candidates",
    forced: meaningfulAlternatives === 0,
    evaluatedAlternatives: ranked.length - 1,
    meaningfulAlternatives,
    thresholdCp: 50,
    lineRanks: ranked.map((line) => line.rank),
  };
};

export const buildBrowserAtomicPositionProof = ({
  fen,
  perspective,
  lines,
}: BrowserAtomicPositionProofInput): BrowserAtomicPositionProof => {
  const board = new Chess(fen);
  const moves = board.moves({ verbose: true });
  const { pins, rays } = atomicPinFacts(board);
  const forcedness = atomicForcedness(moves.length, lines);
  return {
    version: 1,
    fen: board.fen(),
    perspective,
    legal: {
      evidenceId: "atomic:legal",
      sideToMove: atomicColorName(board.turn()),
      inCheck: board.inCheck(),
      checkmate: board.isCheckmate(),
      stalemate: board.isStalemate(),
      legalMoveCount: moves.length,
      moves: moves.map((move) => ({
        san: move.san,
        from: move.from,
        to: move.to,
        ...(move.promotion ? { promotion: move.promotion } : {}),
        capture: move.isCapture(),
        enPassant: move.isEnPassant(),
        castle: move.isKingsideCastle() || move.isQueensideCastle(),
      })),
    },
    material: atomicMaterial(board, perspective),
    squareControl: SQUARES.flatMap((square) => {
      const whiteAttackers = board.attackers(square, "w");
      const blackAttackers = board.attackers(square, "b");
      return whiteAttackers.length || blackAttackers.length
        ? [{ evidenceId: `atomic:square:${square}`, square, whiteAttackers, blackAttackers }]
        : [];
    }),
    pins,
    rays,
    openFiles: ATOMIC_FILES.filter((file) =>
      SQUARES.every((square) => square[0] !== file || board.get(square)?.type !== "p"),
    ).map((file) => ({ evidenceId: `atomic:open-file:${file}`, file })),
    forcingCandidates: atomicForcingCandidates(fen, moves),
    ...(forcedness ? { forcedness } : {}),
  };
};

export interface BrowserEngineSnapshot {
  depth: number;
  lines: BrowserEngineLine[];
}

export type BrowserEngineProvenance =
  | { name: "Stockfish"; version: string; build: string }
  | { name: "Lichess tablebase"; version: "standard"; build: string };

export type TablebaseCategory = "win" | "syzygy-win" | "cursed-win" | "draw" | "blessed-loss" | "syzygy-loss" | "loss";

export interface BrowserTablebaseEvidence {
  outcome: "win" | "draw" | "loss";
  category: TablebaseCategory;
  dtz: number | null;
  dtm: number | null;
  precise: boolean;
  checkmate: boolean;
  stalemate: boolean;
  insufficientMaterial: boolean;
}

export interface BrowserEngineArtifact extends BrowserEngineRequest {
  engine: BrowserEngineProvenance;
  reached: { depth: number; nodes: number; timeMs: number };
  lines: BrowserEngineLine[];
  snapshots?: BrowserEngineSnapshot[];
  tablebase?: BrowserTablebaseEvidence;
  practical?: BrowserPracticalEvidence;
  atomicProof?: BrowserAtomicPositionProof;
  conceptDeltas?: BrowserCoachingConceptDeltas;
  createdAt: string;
}

export type BrowserEngineArtifactValidation = { ok: true } | { ok: false; reason: string };

const invalid = (reason: string): BrowserEngineArtifactValidation => ({ ok: false, reason });
const integerIn = (value: number, minimum: number, maximum: number): boolean =>
  Number.isInteger(value) && value >= minimum && value <= maximum;

const applyMove = (fen: string, move: string): AppliedMove | null =>
  applyLegalMove(fen, move) ?? applySanMove(fen, move);

const sameSquares = (left: readonly Square[], right: readonly Square[]): boolean =>
  left.length === right.length && left.every((square, index) => square === right[index]);

const rayBetween = (from: Square, to: Square): Square[] | null => {
  const fromFile = from.charCodeAt(0) - 97;
  const fromRank = Number(from[1]);
  const fileDelta = Math.sign(to.charCodeAt(0) - 97 - fromFile);
  const rankDelta = Math.sign(Number(to[1]) - fromRank);
  if (!fileDelta && !rankDelta) return null;
  if (fileDelta && rankDelta && Math.abs(to.charCodeAt(0) - from.charCodeAt(0)) !== Math.abs(Number(to[1]) - fromRank))
    return null;
  const ray: Square[] = [from];
  let file = fromFile;
  let rank = fromRank;
  while (file !== to.charCodeAt(0) - 97 || rank !== Number(to[1])) {
    file += fileDelta;
    rank += rankDelta;
    if (file < 0 || file > 7 || rank < 1 || rank > 8) return null;
    ray.push(`${String.fromCharCode(97 + file)}${rank}` as Square);
  }
  return ray;
};

const canonicalAtomicProof = (proof: BrowserAtomicPositionProof): unknown => ({
  ...proof,
  legal: {
    ...proof.legal,
    moves: [...proof.legal.moves].sort((left, right) =>
      `${left.from}${left.to}${left.promotion ?? ""}`.localeCompare(`${right.from}${right.to}${right.promotion ?? ""}`),
    ),
  },
  squareControl: proof.squareControl
    .map((fact) => ({
      ...fact,
      whiteAttackers: [...fact.whiteAttackers].sort(),
      blackAttackers: [...fact.blackAttackers].sort(),
    }))
    .sort((left, right) => left.square.localeCompare(right.square)),
  pins: [...proof.pins].sort((left, right) => left.evidenceId.localeCompare(right.evidenceId)),
  rays: [...proof.rays].sort((left, right) => left.evidenceId.localeCompare(right.evidenceId)),
  openFiles: [...proof.openFiles].sort((left, right) => left.file.localeCompare(right.file)),
  forcingCandidates: [...proof.forcingCandidates].sort((left, right) =>
    left.evidenceId.localeCompare(right.evidenceId),
  ),
  ...(proof.forcedness
    ? { forcedness: { ...proof.forcedness, lineRanks: [...proof.forcedness.lineRanks].sort((a, b) => a - b) } }
    : {}),
});

const validAtomicProof = (artifact: BrowserEngineArtifact): string | null => {
  const proof = artifact.atomicProof;
  if (!proof) return null;
  if (proof.version !== 1) return "Unsupported atomic proof version";
  if (proof.fen !== artifact.source.fen) return "Invalid atomic proof position";
  if (proof.perspective !== artifact.source.playerColor) return "Invalid atomic proof perspective";
  if (
    proof.legal.moves.length > 256 ||
    proof.squareControl.length > 64 ||
    proof.pins.length > 16 ||
    proof.rays.length > 16 ||
    proof.openFiles.length > 8 ||
    proof.forcingCandidates.length > 256
  )
    return "Invalid atomic proof bounds";
  const handles = [
    proof.legal.evidenceId,
    proof.material.evidenceId,
    ...proof.squareControl.map((fact) => fact.evidenceId),
    ...proof.pins.map((fact) => fact.evidenceId),
    ...proof.rays.map((fact) => fact.evidenceId),
    ...proof.openFiles.map((fact) => fact.evidenceId),
    ...proof.forcingCandidates.map((fact) => fact.evidenceId),
    ...(proof.forcedness ? [proof.forcedness.evidenceId] : []),
  ];
  if (new Set(handles).size !== handles.length || handles.some((handle) => !/^atomic:[a-z0-9:-]+$/.test(handle)))
    return "Invalid atomic evidence handle";
  const canonical = buildBrowserAtomicPositionProof({
    fen: artifact.source.fen,
    perspective: artifact.source.playerColor,
    lines: artifact.lines,
  });
  if (canonicalJson(canonicalAtomicProof(proof)) !== canonicalJson(canonicalAtomicProof(canonical)))
    return "Invalid atomic proof facts";

  let board: Chess;
  try {
    board = new Chess(proof.fen);
  } catch {
    return "Invalid atomic proof position";
  }
  const moves = board.moves({ verbose: true });
  const sideToMove = board.turn() === "w" ? "white" : "black";
  if (
    proof.legal.evidenceId !== "atomic:legal" ||
    proof.legal.sideToMove !== sideToMove ||
    proof.legal.inCheck !== board.inCheck() ||
    proof.legal.checkmate !== board.isCheckmate() ||
    proof.legal.stalemate !== board.isStalemate() ||
    proof.legal.legalMoveCount !== moves.length ||
    proof.legal.moves.length !== moves.length
  )
    return "Invalid atomic legal state";
  const legalMoves = new Map(moves.map((move) => [`${move.from}${move.to}${move.promotion ?? ""}`, move]));
  for (const move of proof.legal.moves) {
    const actual = legalMoves.get(`${move.from}${move.to}${move.promotion ?? ""}`);
    if (
      move.san !== actual?.san ||
      move.capture !== actual.isCapture() ||
      move.enPassant !== actual.isEnPassant() ||
      move.castle !== (actual.isKingsideCastle() || actual.isQueensideCastle())
    )
      return "Invalid atomic legal move";
  }

  const pieceValues: Record<PieceSymbol, number> = { p: 100, n: 320, b: 330, r: 500, q: 900, k: 0 };
  const counts = { white: { p: 0, n: 0, b: 0, r: 0, q: 0, k: 0 }, black: { p: 0, n: 0, b: 0, r: 0, q: 0, k: 0 } };
  for (const square of SQUARES) {
    const piece = board.get(square);
    if (piece) counts[piece.color === "w" ? "white" : "black"][piece.type] += 1;
  }
  const materialTotal = (side: BrowserAtomicPieceCounts) =>
    (Object.entries(side) as Array<[PieceSymbol, number]>).reduce(
      (sum, [piece, count]) => sum + pieceValues[piece] * count,
      0,
    );
  const whiteBalance = materialTotal(counts.white) - materialTotal(counts.black);
  if (
    proof.material.evidenceId !== "atomic:material" ||
    canonicalJson(proof.material.white) !== canonicalJson(counts.white) ||
    canonicalJson(proof.material.black) !== canonicalJson(counts.black) ||
    proof.material.balanceCp !== (proof.perspective === "white" ? whiteBalance : -whiteBalance)
  )
    return "Invalid atomic material";

  const controls = new Map(proof.squareControl.map((fact) => [fact.square, fact]));
  if (controls.size !== proof.squareControl.length) return "Invalid atomic square control";
  for (const square of SQUARES) {
    const whiteAttackers = board.attackers(square, "w");
    const blackAttackers = board.attackers(square, "b");
    const fact = controls.get(square);
    if (!whiteAttackers.length && !blackAttackers.length) {
      if (fact) return "Invalid atomic square control";
      continue;
    }
    if (
      fact?.evidenceId !== `atomic:square:${square}` ||
      !sameSquares(fact.whiteAttackers, whiteAttackers) ||
      !sameSquares(fact.blackAttackers, blackAttackers)
    )
      return "Invalid atomic square control";
  }

  const pinRays = new Map(proof.rays.map((ray) => [`${ray.from}:${ray.to}`, ray]));
  if (pinRays.size !== proof.rays.length || proof.rays.length !== proof.pins.length) return "Invalid atomic pin proof";
  for (const pin of proof.pins) {
    const color = pin.color === "white" ? "w" : "b";
    const enemy = color === "w" ? "b" : "w";
    const pinnedPiece = board.get(pin.pinned);
    const attacker = board.get(pin.attacker);
    const ray = pinRays.get(`${pin.attacker}:${pin.king}`);
    const removed = new Chess(proof.fen);
    removed.remove(pin.pinned);
    const expectedRay = rayBetween(pin.attacker, pin.king);
    if (
      pin.evidenceId !== `atomic:pin:${pin.pinned}` ||
      board.get(pin.king)?.type !== "k" ||
      board.get(pin.king)?.color !== color ||
      pinnedPiece?.color !== color ||
      attacker?.color !== enemy ||
      !["b", "r", "q"].includes(attacker.type) ||
      !removed.attackers(pin.king, enemy).includes(pin.attacker) ||
      ray?.evidenceId !== `atomic:ray:${pin.attacker}:${pin.king}` ||
      !expectedRay ||
      !sameSquares(pin.ray, expectedRay) ||
      !sameSquares(ray.squares, pin.ray) ||
      expectedRay
        .slice(1, -1)
        .filter((square) => board.get(square))
        .some((square) => square !== pin.pinned) ||
      !expectedRay.includes(pin.pinned)
    )
      return "Invalid atomic pin proof";
  }

  const openFiles = new Set(proof.openFiles.map((fact) => fact.file));
  if (openFiles.size !== proof.openFiles.length) return "Invalid atomic open files";
  for (const file of ["a", "b", "c", "d", "e", "f", "g", "h"] as const) {
    const open = SQUARES.every((square) => square[0] !== file || board.get(square)?.type !== "p");
    const fact = proof.openFiles.find((item) => item.file === file);
    if (open !== Boolean(fact) || (fact && fact.evidenceId !== `atomic:open-file:${file}`))
      return "Invalid atomic open files";
  }

  const expectedForcingCandidates = moves.flatMap((move) => {
    const after = new Chess(proof.fen);
    after.move(move.san);
    const kind = after.isCheckmate() ? "mate" : after.inCheck() ? "check" : move.isCapture() ? "capture" : null;
    return kind ? [{ move, kind }] : [];
  });
  if (proof.forcingCandidates.length !== expectedForcingCandidates.length) return "Invalid atomic forcing candidates";
  for (const candidate of proof.forcingCandidates) {
    const actual = expectedForcingCandidates.find(
      ({ move }) =>
        candidate.evidenceId === `atomic:forcing:${move.from}${move.to}${move.promotion ?? ""}` &&
        move.san === candidate.san,
    );
    if (
      !actual ||
      candidate.evidenceId !== `atomic:forcing:${actual.move.from}${actual.move.to}${actual.move.promotion ?? ""}` ||
      candidate.san !== actual.move.san ||
      candidate.kind !== actual.kind ||
      candidate.target !== actual.move.captured
    )
      return "Invalid atomic forcing candidates";
  }

  if (proof.forcedness) {
    if (proof.forcedness.evidenceId !== "atomic:forcedness") return "Invalid atomic forcedness";
    if (proof.forcedness.basis === "only_legal_move") {
      if (
        moves.length !== 1 ||
        !proof.forcedness.forced ||
        proof.forcedness.evaluatedAlternatives !== 0 ||
        proof.forcedness.meaningfulAlternatives !== 0 ||
        proof.forcedness.thresholdCp !== null ||
        proof.forcedness.lineRanks.length !== 0
      )
        return "Invalid atomic forcedness";
    } else {
      const ranked = [...artifact.lines].sort((left, right) => left.rank - right.rank);
      const best = ranked[0]?.evaluation.cp;
      if (
        ranked.length < 2 ||
        best === null ||
        best === undefined ||
        ranked.some((line) => line.evaluation.cp === null)
      )
        return "Invalid atomic forcedness";
      const meaningful = ranked.slice(1).filter((line) => Math.abs(line.evaluation.cp! - best) <= 50).length;
      if (
        proof.forcedness.thresholdCp !== 50 ||
        proof.forcedness.evaluatedAlternatives !== ranked.length - 1 ||
        proof.forcedness.meaningfulAlternatives !== meaningful ||
        proof.forcedness.forced !== (meaningful === 0) ||
        JSON.stringify(proof.forcedness.lineRanks) !== JSON.stringify(ranked.map((line) => line.rank))
      )
        return "Invalid atomic forcedness";
    }
  }
  return null;
};

const validConceptDeltas = (artifact: BrowserEngineArtifact): string | null => {
  const comparison = artifact.conceptDeltas;
  if (!comparison) return null;
  if (artifact.kind !== "position") return "Invalid coaching concept evidence";
  if (!artifact.source.gameId || artifact.source.ply === undefined) return "Invalid coaching concept source";
  if (comparison.version !== 1 || comparison.items.length < 1 || comparison.items.length > 3)
    return "Invalid coaching concept bounds";
  const played = applyMove(artifact.source.fen, comparison.playedMove.uci);
  const best = applyMove(artifact.source.fen, comparison.bestMove.uci);
  if (
    !played ||
    !best ||
    played.san !== comparison.playedMove.san ||
    best.san !== comparison.bestMove.san ||
    played.uci === best.uci
  )
    return "Invalid coaching concept moves";
  const concepts = new Set<BrowserCoachingConcept>();
  for (const [index, delta] of comparison.items.entries()) {
    if (
      delta.rank !== index + 1 ||
      !BROWSER_COACHING_CONCEPTS.includes(delta.concept) ||
      concepts.has(delta.concept) ||
      delta.evidenceId !== `atomic:concept:${delta.concept}` ||
      ![delta.before, delta.playedAfter, delta.bestAfter, delta.difference].every(
        (value) => Number.isFinite(value) && Math.abs(value) <= 10_000,
      ) ||
      delta.difference !== delta.bestAfter - delta.playedAfter
    )
      return "Invalid coaching concept delta";
    concepts.add(delta.concept);
  }
  return null;
};

const hasLegalLine = (fen: string, moves: readonly string[]): boolean => {
  let currentFen = fen;
  for (const move of moves) {
    const applied = applyMove(currentFen, move);
    if (!applied) return false;
    currentFen = applied.fen;
  }
  return true;
};

const validSource = (source: BrowserEngineSource): boolean => {
  if (piecesInPosition(source.fen).length < 2) return false;
  if (source.playerColor !== "white" && source.playerColor !== "black") return false;
  if (source.gameId?.trim().length === 0) return false;
  if (source.ply !== undefined && !integerIn(source.ply, 0, 1_000)) return false;
  if (
    source.study &&
    (source.gameId !== undefined ||
      source.ply !== undefined ||
      typeof source.study.sessionId !== "string" ||
      source.study.sessionId.length > 100 ||
      !source.study.sessionId ||
      typeof source.study.runId !== "string" ||
      source.study.runId.length > 100 ||
      !source.study.runId ||
      !integerIn(source.study.ply, 0, 1000) ||
      !["w", "b"].includes(source.study.perspective) ||
      !Array.isArray(source.study.variationUci) ||
      source.study.variationUci.length > 32 ||
      source.study.variationUci.some(
        (move) => typeof move !== "string" || !/^[a-h][1-8][a-h][1-8][qrbn]?$/u.test(move),
      ))
  )
    return false;
  const context = [source.timeControl, source.playerRating, source.opponentRating];
  if (context.every((value) => value === undefined)) return true;
  return (
    context.every((value) => value !== undefined) &&
    ["bullet", "blitz", "rapid", "daily"].includes(source.timeControl!) &&
    integerIn(source.playerRating!, 100, 4_000) &&
    integerIn(source.opponentRating!, 100, 4_000)
  );
};

const validPracticalEvidence = (artifact: BrowserEngineArtifact): boolean => {
  const request = artifact.practicalRequest;
  const evidence = artifact.practical;
  if (!request && !evidence) return artifact.mode !== "practical";
  if (!request || !evidence || artifact.mode !== "practical" || artifact.kind !== "position") return false;
  const { source } = artifact;
  const activeColor = source.fen.split(/\s+/)[1];
  const playerToMove =
    (activeColor === "w" && source.playerColor === "white") || (activeColor === "b" && source.playerColor === "black");
  const expectedActiveRating = playerToMove ? source.playerRating : source.opponentRating;
  const expectedOpponentRating = playerToMove ? source.opponentRating : source.playerRating;
  if (
    request.format !== "rapid" ||
    source.timeControl !== request.format ||
    request.activeRating !== expectedActiveRating ||
    request.opponentRating !== expectedOpponentRating ||
    evidence.format !== request.format ||
    evidence.activeRating !== request.activeRating ||
    evidence.opponentRating !== request.opponentRating ||
    !integerIn(request.activeRating, 100, 4_000) ||
    !integerIn(request.opponentRating, 100, 4_000) ||
    evidence.source.name !== "Maia 2" ||
    evidence.source.version !== "rapid-fp16-v1" ||
    !integerIn(evidence.candidates.length, 1, 5)
  )
    return false;
  const moves = new Set<string>();
  let previous = Infinity;
  for (const candidate of evidence.candidates) {
    if (
      moves.has(candidate.move) ||
      !applyLegalMove(artifact.source.fen, candidate.move) ||
      !Number.isFinite(candidate.probability) ||
      candidate.probability < 0 ||
      candidate.probability > 1 ||
      candidate.probability > previous
    )
      return false;
    moves.add(candidate.move);
    previous = candidate.probability;
  }
  return true;
};

export const validateBrowserEngineArtifact = (artifact: BrowserEngineArtifact): BrowserEngineArtifactValidation => {
  if (artifact.schemaVersion !== 1) return invalid("Unsupported schema version");
  if (!artifact.requestId || artifact.requestId.length > 128) return invalid("Invalid request ID");
  if (!validSource(artifact.source)) return invalid("Invalid source position");
  if (!BROWSER_ENGINE_DEPTHS.includes(artifact.limits.depth)) return invalid("Unsupported depth");
  if (!integerIn(artifact.limits.maxPlies, 1, 32)) return invalid("Invalid ply limit");
  if (!integerIn(artifact.limits.multiPv, 1, 5)) return invalid("Invalid MultiPV limit");
  if (!integerIn(artifact.limits.maxTimeMs, 100, 120_000)) return invalid("Invalid time limit");
  if (typeof artifact.limits.includeWdl !== "boolean") return invalid("Invalid WDL setting");
  if (artifact.mode !== undefined && !BROWSER_ENGINE_MODES.includes(artifact.mode))
    return invalid("Invalid calculation mode");
  if (artifact.mode !== undefined && artifact.kind !== "position") return invalid("Invalid calculation mode");
  if (!validPracticalEvidence(artifact)) return invalid("Invalid practical evidence");
  if (artifact.candidateMoves && !integerIn(artifact.candidateMoves.length, 1, 5))
    return invalid("Invalid candidate moves");
  if (artifact.candidateMoves?.some((move) => !applyMove(artifact.source.fen, move)))
    return invalid("Illegal candidate move");
  if (artifact.kind === "move_comparison") {
    if (
      !artifact.candidateMoves ||
      !integerIn(artifact.candidateMoves.length, 2, 5) ||
      artifact.candidateMoves.length !== artifact.limits.multiPv ||
      artifact.branch !== undefined
    )
      return invalid("Invalid move comparison");
  } else if (artifact.candidateMoves) return invalid("Invalid candidate moves");
  if (artifact.branch && !integerIn(artifact.branch.length, 1, artifact.limits.maxPlies))
    return invalid("Invalid branch");
  if (artifact.branch && !hasLegalLine(artifact.source.fen, artifact.branch)) return invalid("Illegal branch");
  if (artifact.kind === "branch" ? !artifact.branch : artifact.branch) return invalid("Invalid branch");
  const tablebase = artifact.kind === "tablebase";
  if (tablebase && piecesInPosition(artifact.source.fen).length > 7) return invalid("Unsupported tablebase position");
  if (
    tablebase
      ? artifact.engine.name !== "Lichess tablebase" ||
        artifact.engine.version !== "standard" ||
        !artifact.engine.build ||
        !artifact.tablebase
      : artifact.engine.name !== "Stockfish" || !artifact.engine.version || !artifact.engine.build || artifact.tablebase
  )
    return invalid("Invalid engine provenance");
  if (artifact.tablebase) {
    const evidence = artifact.tablebase;
    if (
      !["win", "syzygy-win", "cursed-win", "draw", "blessed-loss", "syzygy-loss", "loss"].includes(evidence.category) ||
      !["win", "draw", "loss"].includes(evidence.outcome) ||
      typeof evidence.precise !== "boolean" ||
      typeof evidence.checkmate !== "boolean" ||
      typeof evidence.stalemate !== "boolean" ||
      typeof evidence.insufficientMaterial !== "boolean" ||
      (evidence.dtz !== null && !integerIn(Math.abs(evidence.dtz), 0, 100_000)) ||
      (evidence.dtm !== null && !integerIn(Math.abs(evidence.dtm), 0, 100_000))
    )
      return invalid("Invalid tablebase evidence");
    const sideOutcome = ["win", "syzygy-win"].includes(evidence.category)
      ? "win"
      : ["loss", "syzygy-loss"].includes(evidence.category)
        ? "loss"
        : "draw";
    const playerToMove = (artifact.source.fen.split(" ")[1] === "w") === (artifact.source.playerColor === "white");
    const playerOutcome = playerToMove
      ? sideOutcome
      : sideOutcome === "win"
        ? "loss"
        : sideOutcome === "loss"
          ? "win"
          : "draw";
    if (evidence.outcome !== playerOutcome) return invalid("Invalid tablebase perspective");
  }
  if (!integerIn(artifact.reached.depth, 1, artifact.limits.depth)) return invalid("Invalid reached depth");
  if (!integerIn(artifact.reached.nodes, 0, Number.MAX_SAFE_INTEGER)) return invalid("Invalid node count");
  if (!integerIn(artifact.reached.timeMs, 0, artifact.limits.maxTimeMs)) return invalid("Invalid elapsed time");
  const terminalTablebase = [
    artifact.tablebase?.checkmate,
    artifact.tablebase?.stalemate,
    artifact.tablebase?.insufficientMaterial,
  ].some(Boolean);
  if (terminalTablebase ? artifact.lines.length !== 0 : !integerIn(artifact.lines.length, 1, artifact.limits.multiPv))
    return invalid("Invalid line count");

  const ranks = new Set<number>();
  for (const line of artifact.lines) {
    if (!integerIn(line.rank, 1, artifact.limits.multiPv) || ranks.has(line.rank)) return invalid("Invalid line rank");
    ranks.add(line.rank);
    if (!integerIn(line.moves.length, 1, artifact.limits.maxPlies)) return invalid("Invalid line length");
    if (!hasLegalLine(artifact.source.fen, line.moves)) return invalid("Illegal principal variation");

    const { cp, mate } = line.evaluation;
    const cpValid = cp !== null && Number.isFinite(cp) && integerIn(cp, -100_000, 100_000);
    const mateValid = mate !== null && integerIn(mate, -1_000, 1_000) && mate !== 0;
    if (cpValid === mateValid) return invalid("Invalid evaluation");

    if (line.wdl) {
      const { win, draw, loss } = line.wdl;
      if (![win, draw, loss].every((value) => integerIn(value, 0, 1_000)) || win + draw + loss !== 1_000)
        return invalid("Invalid WDL");
    }
  }

  if (artifact.snapshots) {
    if (tablebase || !integerIn(artifact.snapshots.length, 1, BROWSER_ENGINE_DEPTHS.length))
      return invalid("Invalid depth snapshots");
    let previousDepth = 0;
    for (const snapshot of artifact.snapshots) {
      if (
        !BROWSER_ENGINE_DEPTHS.includes(snapshot.depth as BrowserEngineDepth) ||
        snapshot.depth <= previousDepth ||
        snapshot.depth > artifact.reached.depth
      )
        return invalid("Invalid depth snapshots");
      const validation = validateBrowserEngineArtifact({
        ...artifact,
        reached: { ...artifact.reached, depth: snapshot.depth },
        lines: snapshot.lines,
        snapshots: undefined,
        mode: undefined,
        atomicProof: undefined,
      });
      if (!validation.ok) return invalid("Invalid depth snapshots");
      previousDepth = snapshot.depth;
    }
    const last = artifact.snapshots.at(-1);
    if (last?.depth !== artifact.reached.depth || JSON.stringify(last?.lines) !== JSON.stringify(artifact.lines))
      return invalid("Invalid depth snapshots");
  } else if (artifact.mode === "robustness") return invalid("Invalid depth snapshots");

  const atomicProofError = validAtomicProof(artifact);
  if (atomicProofError) return invalid(atomicProofError);
  const conceptDeltaError = validConceptDeltas(artifact);
  if (conceptDeltaError) return invalid(conceptDeltaError);
  if (!Number.isFinite(Date.parse(artifact.createdAt))) return invalid("Invalid creation time");
  return { ok: true };
};
